import { ComponentPropsWithoutRef, ReactNode, forwardRef } from 'react';
import { cn } from '@v2/shared/lib/utils';

type Props = Omit<ComponentPropsWithoutRef<'input'>, 'type'> & {
  label: ReactNode;
  /** Shown under the label, for the sentence a checkbox usually needs. */
  description?: ReactNode;
  error?: string;
};

/**
 * A checkbox and its label.
 *
 * Not built on FormField like the other controls: those stack a label above a
 * control, and a checkbox reads the other way round. The label wraps the input so
 * the whole line is a hit target, which matters most on the phone.
 *
 * `accent-primary` rather than a Tailwind colour, because four brands mount these
 * components and only two of them are red — see styles/v2.css.
 */
const FormCheckbox = forwardRef<HTMLInputElement, Props>(
  ({ label, description, id, error, className, ...props }, ref) => (
    <div className="mb-4">
      <label className="flex items-start cursor-pointer">
        <input
          ref={ref}
          type="checkbox"
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && id ? `${id}-error` : undefined}
          className={cn('mt-1 mr-3 h-4 w-4 shrink-0 accent-primary border-gray-300', className)}
          {...props}
        />
        <span>
          <span className="font-semibold">{label}</span>
          {description && <span className="block text-sm text-slate-600">{description}</span>}
        </span>
      </label>

      {error && (
        <span id={id ? `${id}-error` : undefined} className="text-red-600 text-sm mt-1 block">
          {error}
        </span>
      )}
    </div>
  )
);

FormCheckbox.displayName = 'FormCheckbox';

export default FormCheckbox;
